/**
 * Contas e links (frame `65:2`, `Card/Presença da campanha`): one card per platform handle or
 * campaign site, with add and remove. `platform: null` (`PresenceOut`, `api`'s
 * `routers/campaigns.py`) is the campaign's own site; every other value is a handle on that
 * platform, shown with what the latest profile collection read (picture, bio, followers,
 * following, posts, verified), or a pending line until one has.
 *
 * Reads through `useCampaignRecord`, the same cache entry `CampaignRecord` already holds, so
 * mounting this panel costs no extra request there; add and remove write that same cache entry
 * (`./api/queries`) rather than refetching the whole record.
 */
import { BadgeCheck, Globe, Megaphone, Plus, Trash2 } from 'lucide-react';
import { useId, useState, type FormEvent, type ReactNode } from 'react';

import { PageSection } from '../../../chrome/page';
import { ConfirmDialog } from '../../../chrome/form/ConfirmDialog';
import { IconHint } from '../../../chrome/hint';
import { FormDialog } from '../../../chrome/form/FormDialog';
import { EmptyState, ErrorState, QueryStates } from '../../../chrome/state';
import { Avatar, AvatarFallback, AvatarImage } from '../../../ui/avatar';
import { Button } from '../../../ui/button';
import { getLocale } from '../../../i18n';
import { compact } from '../../../i18n/format';
import { Field, FieldLabel } from '../../../ui/field';
import { IconButton } from '../../../ui/icon-button';
import type { ApiFailure } from '../../../lib/http/errors';
import { Input } from '../../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { useAddPresence, useCampaignRecord, useRemovePresence } from './api/queries';
import type { Presence as PresenceRow } from './api/schemas';
import { rm } from './recordMessages';

type Kind = 'instagram' | 'tiktok' | 'site';

function kindOf(row: PresenceRow): Kind {
  return row.platform ?? 'site';
}

/** The frame's own display convention: `@handle` for a platform row, the site's URL with its
 *  scheme dropped -- `address` itself keeps the full `http(s)` URL, since that is what a site row
 *  round-trips to the server. */
function titleOf(row: PresenceRow): string {
  return row.platform === null ? row.address.replace(/^https?:\/\//, '') : `@${row.address}`;
}

function subtitleOf(row: PresenceRow): string {
  if (row.platform === null) return rm('presence.subtitle.site');
  const platform = rm(
    row.platform === 'instagram' ? 'presence.platform.instagram' : 'presence.platform.tiktok',
  );
  return rm('presence.subtitle.platform', { platform });
}

function RowIcon(props: { kind: Kind }): ReactNode {
  const className = 'size-4.25 shrink-0 text-muted-foreground';
  return props.kind === 'site' ? (
    <Globe aria-hidden className={className} />
  ) : (
    <Megaphone aria-hidden className={className} />
  );
}

function Figure(props: { value: number | null; label: string }): ReactNode {
  if (props.value === null) return null;
  return (
    <span className="flex items-baseline gap-1 text-caption text-muted-foreground">
      <span data-numeric className="text-label text-foreground">
        {compact(props.value, getLocale())}
      </span>
      <span>{props.label}</span>
    </span>
  );
}

function ProfileBlock(props: { row: PresenceRow }): ReactNode {
  const profile = props.row.profile ?? null;
  if (profile === null) {
    return (
      <div className="flex flex-col text-caption text-muted-foreground">
        <span className="truncate">{subtitleOf(props.row)}</span>
        <span className="truncate">{rm('presence.pending')}</span>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      {profile.display_name !== null && (
        <p className="truncate text-caption text-muted-foreground">{profile.display_name}</p>
      )}
      {profile.bio !== null && (
        <p className="line-clamp-2 text-caption text-foreground">{profile.bio}</p>
      )}
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        <Figure value={profile.followers} label={rm('presence.followers')} />
        <Figure value={profile.following} label={rm('presence.following')} />
        <Figure value={profile.posts_count} label={rm('presence.posts')} />
      </div>
    </div>
  );
}

function PresenceAvatar(props: { row: PresenceRow }): ReactNode {
  const url = props.row.profile?.avatar_url ?? null;
  if (props.row.platform === null || url === null) return <RowIcon kind={kindOf(props.row)} />;
  return (
    <Avatar className="size-10 shrink-0">
      <AvatarImage src={url} alt="" className="object-cover" />
      <AvatarFallback>
        <RowIcon kind={kindOf(props.row)} />
      </AvatarFallback>
    </Avatar>
  );
}

function PresenceRowItem(props: {
  row: PresenceRow;
  editable: boolean;
  removing: boolean;
  removeFailure: ApiFailure | null;
  onRemove: () => Promise<unknown>;
}): ReactNode {
  const { row } = props;
  const title = titleOf(row);
  const [confirmOpen, setConfirmOpen] = useState(false);
  return (
    <div className="flex w-full min-w-0 items-start gap-3 rounded-md border bg-surface-brief p-3">
      <PresenceAvatar row={row} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="flex min-w-0 items-center gap-1 text-label text-foreground">
          <span className="truncate">{title}</span>
          {row.profile?.verified === true && (
            <BadgeCheck
              role="img"
              aria-label={rm('presence.verified')}
              className="size-4 shrink-0 text-primary"
            />
          )}
          {row.profile?.is_private === true && (
            <span className="shrink-0 rounded-full bg-secondary px-1.5 text-caption-sm text-muted-foreground">
              {rm('presence.private')}
            </span>
          )}
        </p>
        {row.platform === null ? (
          <p className="truncate text-caption text-muted-foreground">{subtitleOf(row)}</p>
        ) : (
          <ProfileBlock row={row} />
        )}
      </div>
      {props.editable && (
        <>
          <IconHint label={rm('presence.remove', { address: title })}>
            <IconButton
              tone="quiet"
              label={rm('presence.remove', { address: title })}
              onClick={() => setConfirmOpen(true)}
            >
              <Trash2 aria-hidden />
            </IconButton>
          </IconHint>
          <ConfirmDialog
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            title={rm('presence.remove.confirm.title', { address: title })}
            description={rm('presence.remove.confirm.description')}
            confirmLabel={rm('presence.remove.confirm.action')}
            destructive
            pending={props.removing}
            failure={props.removeFailure}
            onConfirm={props.onRemove}
          />
        </>
      )}
    </div>
  );
}

function AddPresence(props: { campaignId: string }): ReactNode {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<Kind>('instagram');
  const [address, setAddress] = useState('');
  const mutation = useAddPresence(props.campaignId);
  const kindId = useId();
  const addressId = useId();

  function handleOpenChange(next: boolean): void {
    setOpen(next);
    if (!next) {
      setKind('instagram');
      setAddress('');
      mutation.reset();
    }
  }

  function submit(event: FormEvent<HTMLFormElement>): Promise<unknown> {
    event.preventDefault();
    return mutation.mutateAsync({
      platform: kind === 'site' ? null : kind,
      address: address.trim(),
    });
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={rm('presence.add.title')}
      submitLabel={rm('presence.add.submit')}
      pending={mutation.isPending}
      submitDisabled={address.trim() === ''}
      failure={mutation.error}
      onSubmit={submit}
      trigger={
        <Button type="button" variant="outline" className="w-full">
          <Plus aria-hidden />
          {rm('presence.add.trigger')}
        </Button>
      }
    >
      <Field>
        <FieldLabel htmlFor={kindId}>{rm('presence.add.kind.label')}</FieldLabel>
        <Select value={kind} onValueChange={(value) => setKind(value as Kind)}>
          <SelectTrigger id={kindId} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="instagram">{rm('presence.platform.instagram')}</SelectItem>
            <SelectItem value="tiktok">{rm('presence.platform.tiktok')}</SelectItem>
            <SelectItem value="site">{rm('presence.add.kind.site')}</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field>
        <FieldLabel htmlFor={addressId}>
          {kind === 'site' ? rm('presence.add.address.site') : rm('presence.add.address.handle')}
        </FieldLabel>
        <Input
          id={addressId}
          value={address}
          onChange={(event) => setAddress(event.target.value)}
          placeholder={
            kind === 'site'
              ? rm('presence.add.address.site.placeholder')
              : rm('presence.add.address.handle.placeholder')
          }
        />
      </Field>
    </FormDialog>
  );
}

export function Presence(props: { campaignId: string; editable: boolean }): ReactNode {
  const query = useCampaignRecord(props.campaignId);
  const removeMutation = useRemovePresence(props.campaignId);

  return (
    <PageSection title={rm('presence.title')}>
      <QueryStates
        query={query}
        isEmpty={(data) => data.presences.length === 0}
        empty={
          <>
            <EmptyState title={rm('presence.empty')} />
            {props.editable && <AddPresence campaignId={props.campaignId} />}
          </>
        }
      >
        {(campaign) => (
          <>
            {removeMutation.error !== null && <ErrorState failure={removeMutation.error.failure} />}
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
              {campaign.presences.map((row) => (
                <PresenceRowItem
                  key={row.id}
                  row={row}
                  editable={props.editable}
                  removing={removeMutation.isPending && removeMutation.variables === row.id}
                  removeFailure={
                    removeMutation.variables === row.id
                      ? (removeMutation.error?.failure ?? null)
                      : null
                  }
                  onRemove={() => removeMutation.mutateAsync(row.id)}
                />
              ))}
            </div>
            {props.editable && <AddPresence campaignId={props.campaignId} />}
          </>
        )}
      </QueryStates>
    </PageSection>
  );
}
